import { ChartHandle, LineChart, LineChartOption } from '@creditsights/styleguide-web';
import React, { ReactElement, useEffect, useMemo, useRef } from 'react';
import classNames from 'classnames/bind';
import { useWindowResizeListener } from '../../../../hooks/common';
import { CqsMetric } from '../../../../models/RiskProducts';
import { formatCommonDate } from '../../../../utils/dateUtils';
import MetricTags from '../MetricTags/MetricTags';
import { getMetricTag } from '../RiskProductsWidget.logic';
import { getCqsChartConfig } from './CqsChart.logic';
import { infoTooltipText } from './CqsChart.presentational.constants';
import styles from '../CqsCreChart.module.scss';
import { TimeFrameOptions } from '../RiskProductsWidget.types';

const cx = classNames.bind(styles);

interface CqsChartProps {
  cqsMetrics: CqsMetric[];
  timeframe?: TimeFrameOptions;
}

const CqsChart = ({ cqsMetrics, timeframe }: CqsChartProps): ReactElement => {
  const mostRecentCqsMetric = cqsMetrics[0];
  const mostRecentCQS = getMetricTag(mostRecentCqsMetric.cqs);
  const mostRecentACR = getMetricTag(mostRecentCqsMetric.acrUngrouped);
  const mostRecentActionDay = getMetricTag(formatCommonDate(mostRecentCqsMetric.calculationDate));
  const chartHandle = useRef<ChartHandle>(null);
  const printChartHandle = useRef<ChartHandle>(null);

  useEffect(() => {
    return () => {
      chartHandle.current?.chartInstance?.dispose();
      printChartHandle.current?.chartInstance?.dispose();
    };
  }, []);

  useWindowResizeListener(() => {
    chartHandle.current?.chartInstance?.resize();
    printChartHandle.current?.chartInstance?.resize();
  });

  const chartOptions: LineChartOption = useMemo(() => {
    return getCqsChartConfig(cqsMetrics, timeframe);
  }, [cqsMetrics, timeframe]);

  return (
    <div className={cx('body')} data-testid="cqs-chart">
      <div className={cx('for-screen')}>
        <LineChart ref={chartHandle} option={chartOptions} />
      </div>
      <div className={cx('for-print')}>
        <LineChart ref={printChartHandle} option={chartOptions} />
      </div>
      <MetricTags
        firstTag={`CQS: ${mostRecentCQS}`}
        secondTag={`ACR: ${mostRecentACR}`}
        date={mostRecentActionDay}
        infoTooltipText={infoTooltipText}
      />
    </div>
  );
};

export default CqsChart;
